import type { ErrorEnvelope } from '@signbento/contracts'
import {
  NATIVE_CLIENT_HEADERS,
  clearNativeSession,
  isNativeApp,
  readNativeSession,
} from './native-session.js'

/**
 * API client — M3.
 * One fetch-based transport that speaks the versioned contracts and turns the
 * canonical error envelope into a typed error, so callers never parse prose or leak
 * provider messages. A transport seam keeps the client testable without a server.
 */

export interface ApiTransport {
  request<TResponse>(input: {
    readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
    readonly path: string
    readonly body?: unknown
    readonly client_request_id?: string
    readonly signal?: AbortSignal
    /** Extra headers for this one request, e.g. a single-use Turnstile token. */
    readonly headers?: Readonly<Record<string, string>>
  }): Promise<TResponse>
}

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly retryable: boolean
  readonly recovery_action: string | null
  readonly correlation_id: string | null
  readonly field_errors: ErrorEnvelope['field_errors']
  /** The service's `Retry-After`, in seconds, when it said how long to wait (a rate limit). */
  readonly retry_after_seconds: number | null

  constructor(input: {
    code: string
    status: number
    message: string
    retryable: boolean
    recovery_action?: string
    correlation_id?: string
    field_errors?: ErrorEnvelope['field_errors']
    retry_after_seconds?: number
  }) {
    super(input.message)
    this.name = 'ApiError'
    this.code = input.code
    this.status = input.status
    this.retryable = input.retryable
    this.recovery_action = input.recovery_action ?? null
    this.correlation_id = input.correlation_id ?? null
    this.field_errors = input.field_errors
    this.retry_after_seconds = input.retry_after_seconds ?? null
  }
}

/** `Retry-After` as seconds; only the delta form the API sends, anything else is ignored. */
export function retryAfterSeconds(header: string | null): number | null {
  if (header === null || !/^\d{1,6}$/.test(header.trim())) return null
  return Number(header.trim())
}

/** Only an explicit invalid-session response ends the native bearer session. */
export function sessionEnded(code: unknown): boolean {
  return code === 'OP_UNAUTHENTICATED'
}

export interface FetchTransportOptions {
  readonly base_url: string
  readonly fetch?: typeof fetch
  /** Extra headers, e.g. an origin header for cookie-authenticated mutations. */
  readonly headers?: Readonly<Record<string, string>>
}

export function createFetchTransport(options: FetchTransportOptions): ApiTransport {
  const doFetch = options.fetch ?? fetch
  const base = options.base_url.replace(/\/$/, '')

  return {
    async request<TResponse>(input: {
      readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
      readonly path: string
      readonly body?: unknown
      readonly client_request_id?: string
      readonly signal?: AbortSignal
      readonly headers?: Readonly<Record<string, string>>
    }): Promise<TResponse> {
      // The installed app authenticates with its bearer session; a browser sends its cookie.
      const bearer = readNativeSession()
      const response = await doFetch(`${base}${input.path}`, {
        method: input.method,
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          ...(bearer === null ? {} : { authorization: `Bearer ${bearer}` }),
          ...(isNativeApp() ? NATIVE_CLIENT_HEADERS : {}),
          ...(input.client_request_id === undefined
            ? {}
            : { 'x-client-request-id': input.client_request_id }),
          ...options.headers,
          ...input.headers,
        },
        ...(input.body === undefined ? {} : { body: JSON.stringify(input.body) }),
        ...(input.signal === undefined ? {} : { signal: input.signal }),
      })

      const text = await response.text()
      const parsed: unknown = text === '' ? null : safeJson(text)

      if (!response.ok) {
        const envelope = parsed as Partial<ErrorEnvelope> | null
        // Step-up also uses 401: it requires a fresh verification, not a new full session.
        if (response.status === 401 && bearer !== null && sessionEnded(envelope?.code)) {
          clearNativeSession()
        }
        const retryAfter = retryAfterSeconds(response.headers?.get('retry-after') ?? null)
        throw new ApiError({
          code: typeof envelope?.code === 'string' ? envelope.code : 'OP_UNEXPECTED',
          status: response.status,
          message:
            typeof envelope?.message_key === 'string' ? envelope.message_key : 'Request failed',
          retryable: envelope?.retryable === true,
          ...(typeof envelope?.recovery_action === 'string'
            ? { recovery_action: envelope.recovery_action }
            : {}),
          ...(typeof envelope?.correlation_id === 'string'
            ? { correlation_id: envelope.correlation_id }
            : {}),
          ...(envelope?.field_errors === undefined ? {} : { field_errors: envelope.field_errors }),
          ...(retryAfter === null ? {} : { retry_after_seconds: retryAfter }),
        })
      }

      return parsed as TResponse
    },
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

/** True when a failed call may be retried with the same idempotency key. */
export function isRetryable(error: unknown): boolean {
  return error instanceof ApiError && error.retryable
}

/** A stale-revision or lease conflict is a user-visible state change, not a retry. */
export function isConflict(error: unknown): boolean {
  return (
    error instanceof ApiError && (error.status === 409 || error.code === 'IDEMPOTENCY_CONFLICT')
  )
}
